/**
 * Bridge between the apply-form funnel and the site counters.
 *
 * `analytics.ts` stays provider-agnostic on purpose; this is the one place that
 * knows both sides. It is also the only place where funnel data crosses into a
 * third party, so the mapping is written out key by key: a new field on
 * `ApplyAnalyticsProps` does not reach the counters until someone adds it here
 * deliberately. No spread, no pass-through — that is what keeps the strict PII
 * rule in `analytics.ts` a property of the code rather than of a review.
 *
 * Event names are reused verbatim as the GTM `dataLayer` event, the GA4 event
 * name and the Yandex.Metrika goal id.
 */

import { trackEvent } from '../../shared/lib/web-analytics';
import { registerApplyAnalyticsSink } from './analytics';
import type { AnalyticsEventProps } from '../../shared/lib/web-analytics';
import type { ApplyAnalyticsProps, ApplyAnalyticsSink } from './analytics';

function toEventProps(props?: ApplyAnalyticsProps): AnalyticsEventProps | undefined {
  const mapped: Record<string, string | number> = {};

  if (props?.fields?.length) {
    // Counters take flat values only, so the field categories travel as one
    // string. GA4 truncates a parameter value at 100 characters and an empty
    // form invalidates every field at once, so the count is sent alongside: it
    // stays exact even when the list is cut short.
    mapped.fields = props.fields.join(',');
    mapped.fields_count = props.fields.length;
  }
  if (props?.errorCategory) mapped.error_category = props.errorCategory;

  return Object.keys(mapped).length > 0 ? mapped : undefined;
}

/** Sends one funnel event to every enabled counter. */
export const applyWebAnalyticsSink: ApplyAnalyticsSink = (event, props) => {
  trackEvent(event, toEventProps(props));
};

/** Wires the funnel to the counters. Returns an unsubscribe function. */
export function registerApplyWebAnalyticsSink(): () => void {
  return registerApplyAnalyticsSink(applyWebAnalyticsSink);
}
